import {Controller, useForm} from 'react-hook-form';
import React, {useEffect, useState} from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
} from '@material-ui/core';
import {ISupplierUserProfile} from 'constants/interfaces';
import {getDepotName} from 'screens/Dashboard/Team/TeamTableSetup';
import SearchIcon from '@material-ui/icons/Search';

export type ChangeAccountManagerFormSubmission = {
  supplierId: string;
};

export function ChangeAccountManagerForm(props: {
  team: ISupplierUserProfile[];
  isLoading: boolean;
  submitButtonTitle: string;
  onSubmit: (submission: ChangeAccountManagerFormSubmission) => any;
  cancelButtonTitle?: string;
  onCancel?: () => any;
}) {
  type Submission = ChangeAccountManagerFormSubmission;

  // https://levelup.gitconnected.com/using-react-hook-form-with-material-ui-components-ba42ace9507a
  const {handleSubmit, control} = useForm<Submission>();

  const [team, setTeam] = useState<ISupplierUserProfile[]>([]);
  const [teamSearchTerm, setTeamSearchTerm] = useState<string>();

  const defaultTeam = () => {
    const fullName = (teamMember: ISupplierUserProfile) =>
      teamMember.name + ' ' + teamMember.surname;
    return props.team.sort((a, b) => fullName(a).localeCompare(fullName(b)));
  };

  useEffect(() => setTeam(defaultTeam()), [props.team]);

  // search team
  useEffect(() => {
    if (!teamSearchTerm) {
      setTeam(defaultTeam());
      return;
    }
    const filtered = props.team.filter((teamMember) => {
      const fullName = teamMember.name + ' ' + teamMember.surname;
      return (
        fullName.toLowerCase().includes(teamSearchTerm.toLowerCase()) ||
        getDepotName(teamMember)
          ?.toLowerCase()
          .includes(teamSearchTerm.toLowerCase())
      );
    });
    setTeam(filtered);
  }, [teamSearchTerm]);

  const onSubmit = (submission: Submission) => {
    props.onSubmit({
      supplierId: submission.supplierId,
    });
  };

  return (
    <div className="generic-form-container">
      <div className="form-container">
        <form
          className="form"
          onSubmit={handleSubmit(onSubmit)}
          style={{marginTop: 0}}
        >
          <Grid container spacing={2} className="form-section">
            <Grid item xs={12}>
              <Controller
                name="supplierId"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <>
                    <Box marginBottom={'1rem'}>
                      <TextField
                        className="input"
                        label=""
                        value={teamSearchTerm}
                        onChange={(event) =>
                          setTeamSearchTerm(event.target.value)
                        }
                        variant="outlined"
                        fullWidth
                        disabled={props.isLoading}
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                    <FormControl
                      component="fieldset"
                      error={!!error}
                      style={{width: '100%'}}
                    >
                      <List
                        disablePadding
                        style={{maxHeight: '15rem', overflowY: 'scroll'}}
                      >
                        {team.map((teamMember) => (
                          <ListItem
                            key={teamMember.id}
                            dense
                            button
                            onClick={() => onChange(teamMember.id)}
                          >
                            <ListItemIcon>
                              <Checkbox
                                edge="start"
                                checked={teamMember.id === value}
                                tabIndex={-1}
                                disableRipple
                                color="primary"
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={`${teamMember.name}  ${teamMember.surname}`}
                              secondary={`${getDepotName(teamMember)}`}
                            />
                          </ListItem>
                        ))}
                      </List>
                      <FormHelperText>{error?.message || null}</FormHelperText>
                    </FormControl>
                  </>
                )}
                rules={{
                  required: 'Account manager required',
                }}
              />
            </Grid>
          </Grid>
          <Grid
            container
            spacing={2}
            style={{paddingTop: '1rem', paddingBottom: '1rem'}}
          >
            <Grid item xs={12} sm={6}>
              {props.cancelButtonTitle && (
                <Button
                  className={'secondary-btn'}
                  variant="contained"
                  disableElevation
                  fullWidth
                  disabled={props.isLoading}
                  onClick={props.onCancel}
                >
                  {props.cancelButtonTitle}
                </Button>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                className={'primary-btn'}
                type="submit"
                variant="contained"
                disableElevation
                fullWidth
                color="primary"
                disabled={props.isLoading}
              >
                {props.isLoading ? (
                  <CircularProgress color={'primary'} size={24} thickness={6} />
                ) : (
                  props.submitButtonTitle
                )}
              </Button>
            </Grid>
          </Grid>
        </form>
      </div>
    </div>
  );
}
